import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  SkeletonCircle,
  Text,
  VStack,
} from '@chakra-ui/react';
import flag from 'assets/flag.webp';
import { Contact, User } from 'lucide-react';
import { Controller, useWatch } from 'react-hook-form';
import type { Control, FieldErrors } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import type { CartTicketInfo } from 'context/CartContext/types';
import type { ContactSource, FormValues } from './TicketHolderForm';

type HolderCardProps = {
  ticket: CartTicketInfo;
  index: number;
  control: Control<FormValues>;
  errors: FieldErrors<FormValues>;
  dialingCode: string;
  contactSource: ContactSource;
  /** Whether a contact picker can actually open; when false the card is manual-entry only. */
  canPickContact: boolean;
  isCollapsed: boolean;
  /**
   * True while the buyer's own contact is still in flight and this card is the one it will fill.
   * Shows a skeleton instead of an empty form, so the fields are not offered and then overwritten.
   */
  isLoading?: boolean;
  /** "حفظ التفاصيل" — switches this card to the compact avatar/name/phone view. */
  onSaveDetails: () => void;
  /** Called when the buyer types in a name or phone, so the container can retag `source`. */
  onManualEdit: () => void;
  onEdit: () => void;
  onChooseContact: () => void;
};

// Pick-contact button copy per resolved source (TZ-CO-2). Same button, same behavior entry
// point — only the label differs by branch.
const contactButtonMessage: Record<ContactSource, { id: string; defaultMessage: string }> = {
  device: { id: 'checkout.chooseFromContacts', defaultMessage: 'Choose from contacts' },
  savedHolders: { id: 'checkout.chooseFromSavedContacts', defaultMessage: 'Saved contacts' },
};

export const HolderCard = ({
  ticket,
  index,
  control,
  errors,
  dialingCode,
  contactSource,
  canPickContact,
  isCollapsed,
  isLoading = false,
  onSaveDetails,
  onManualEdit,
  onEdit,
  onChooseContact,
}: HolderCardProps) => {
  const { formatMessage } = useIntl();
  const holderName = useWatch({ control, name: `ticketHolders.${index}.name` as const });
  const holderPhone = useWatch({ control, name: `ticketHolders.${index}.phoneNumber` as const });
  const fieldError = errors?.ticketHolders?.[index];

  return (
    <Flex
      direction='column'
      dir='rtl'
      bg='white'
      borderRadius='16px'
      borderWidth='1px'
      borderColor='dark.D100'
      overflow='hidden'
      mb='16px'
      boxShadow='0 5px 5px rgba(0, 0, 0, 0.02)'
    >
      {/* Tier header bar — colored by the ticket category. */}
      <Flex
        bg={ticket.categoryColor || 'dark.D900'}
        color='white'
        px='16px'
        py='10px'
        align='center'
        justify='space-between'
        gap='8px'
      >
        <Text textStyle='bodySmallSemibold' fontWeight='700' dir='ltr'>
          <FormattedMessage
            id='checkout.ticket.ticketNo'
            defaultMessage='Ticket {ticketNo}'
            values={{ ticketNo: index + 1 }}
          />
        </Text>
        <HStack spacing='8px' minW='0'>
          <Text textStyle='bodySmallMedium' color='whiteAlpha.900' noOfLines={1}>
            <FormattedMessage
              id='checkout.ticketStand'
              defaultMessage='Stand {sector}'
              values={{ sector: ticket.sector }}
            />
          </Text>
          <Badge
            bg='whiteAlpha.300'
            color='white'
            borderRadius='full'
            px='10px'
            py='2px'
            textTransform='none'
            fontWeight='600'
            flexShrink={0}
          >
            {ticket.ticketLabel || ticket.category}
          </Badge>
        </HStack>
      </Flex>

      <Box p={{ base: '16px', md: '20px' }}>
        {isLoading ? (
          /* This card is about to be filled from the buyer's own contact, so it shows the shape of
             the compact view rather than an empty form that would be replaced a moment later. */
          <HStack justify='space-between' align='center' gap='12px' data-testid='holder-loading'>
            <HStack spacing='12px' minW='0' flex='1'>
              <SkeletonCircle size='40px' flexShrink={0} />
              <VStack align='stretch' spacing='6px' flex='1' maxW='180px'>
                <Skeleton height='12px' width='70%' />
                <Skeleton height='10px' width='50%' />
              </VStack>
            </HStack>
            <Skeleton height='32px' width='64px' borderRadius='full' flexShrink={0} />
          </HStack>
        ) : isCollapsed ? (
          <HStack justify='space-between' align='center' gap='12px'>
            <HStack spacing='12px' minW='0'>
              <Flex
                boxSize='40px'
                borderRadius='full'
                bg='dark.D100'
                align='center'
                justify='center'
                flexShrink={0}
              >
                <User size={20} color='#565C5C' aria-hidden />
              </Flex>
              <VStack align='end' spacing='2px' minW='0'>
                <Text textStyle='bodySmallSemibold' color='dark.D900' noOfLines={1}>
                  {holderName}
                </Text>
                <Text textStyle='bodySmallRegular' color='dark.D500' dir='ltr'>
                  {dialingCode} {holderPhone}
                </Text>
              </VStack>
            </HStack>
            <Button
              type='button'
              variant='outline'
              size='sm'
              borderRadius='full'
              borderColor='dark.D150'
              color='dark.D700'
              flexShrink={0}
              onClick={onEdit}
            >
              <FormattedMessage id='checkout.change' defaultMessage='Change' />
            </Button>
          </HStack>
        ) : (
          <VStack spacing='16px' align='stretch'>
            {/* Pick-a-contact shortcut, shown only when a picker can actually open (TZ-CO-18).
                The "or enter manually" divider goes with it: with no button above it, it would be
                separating the manual fields from nothing. */}
            {canPickContact && (
              <>
                <Button
                  type='button'
                  bg='dark.D900'
                  color='white'
                  borderRadius='full'
                  h='48px'
                  w='100%'
                  leftIcon={<Contact size={18} aria-hidden />}
                  _hover={{ bg: 'dark.D800' }}
                  _active={{ bg: 'dark.D700' }}
                  onClick={onChooseContact}
                >
                  <FormattedMessage {...contactButtonMessage[contactSource]} />
                </Button>

                <HStack spacing='12px'>
                  <Divider borderColor='dark.D150' />
                  <Text textStyle='bodySmallMedium' color='dark.D400' whiteSpace='nowrap'>
                    <FormattedMessage
                      id='checkout.orEnterManually'
                      defaultMessage='or enter manually'
                    />
                  </Text>
                  <Divider borderColor='dark.D150' />
                </HStack>
              </>
            )}

            {/* Name */}
            <VStack align='stretch' spacing='6px'>
              <Controller
                name={`ticketHolders.${index}.name` as const}
                control={control}
                defaultValue=''
                render={({ field }) => (
                  <Input
                    aria-label='name'
                    placeholder={formatMessage({
                      id: 'checkout.holderNamePlaceholder',
                      defaultMessage: 'Ticket holder name (full name)',
                    })}
                    textAlign='right'
                    maxLength={50}
                    borderRadius='12px'
                    focusBorderColor={fieldError?.name ? 'red' : 'green.G500'}
                    aria-invalid={Boolean(fieldError?.name)}
                    aria-errormessage={!fieldError?.name ? undefined : `nameError-${index}`}
                    {...field}
                    onChange={(event) => {
                      field.onChange(event);
                      onManualEdit();
                    }}
                  />
                )}
              />
              {fieldError?.name && (
                <Text textStyle='bodySmallMedium' color='red' id={`nameError-${index}`}>
                  {formatMessage({
                    id: fieldError.name.message || 'checkout.name.invalid',
                    defaultMessage: 'Please enter your full name (first and last name)',
                  })}
                </Text>
              )}
            </VStack>

            {/* Phone */}
            <VStack align='stretch' spacing='6px'>
              <InputGroup>
                <Controller
                  name={`ticketHolders.${index}.phoneNumber` as const}
                  control={control}
                  defaultValue=''
                  render={({ field }) => (
                    <>
                      <InputLeftElement pointerEvents='none' mx={1}>
                        <Image src={flag} boxSize={6} alt='image of Iraqi flag' />
                      </InputLeftElement>
                      <InputLeftElement
                        pointerEvents='none'
                        borderLeft='1px solid'
                        borderColor='dark.D150'
                        margin='8px 0 0 45px'
                        height={6}
                        paddingLeft='15px'
                      >
                        {dialingCode}
                      </InputLeftElement>
                      <Input
                        onWheel={(e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur()}
                        placeholder={formatMessage({
                          id: 'checkout.holderPhonePlaceholder',
                          defaultMessage: 'WhatsApp mobile number',
                        })}
                        aria-label='phoneNumber'
                        paddingLeft='95px'
                        type='tel'
                        borderRadius='12px'
                        color='dark.D900'
                        focusBorderColor={fieldError?.phoneNumber ? 'red' : 'green.G500'}
                        aria-invalid={Boolean(fieldError?.phoneNumber)}
                        aria-errormessage={
                          !fieldError?.phoneNumber ? undefined : `phoneNumberError-${index}`
                        }
                        {...field}
                        onChange={(event) => {
                          field.onChange(event);
                          onManualEdit();
                        }}
                      />
                    </>
                  )}
                />
              </InputGroup>
              {fieldError?.phoneNumber && (
                <Text textStyle='bodySmallMedium' color='red' id={`phoneNumberError-${index}`}>
                  {formatMessage({
                    id: fieldError.phoneNumber.message || 'checkout.phoneNumber.invalid',
                    defaultMessage: 'Please enter a valid Iraqi phone number (7XXXXXXXXX)',
                  })}
                </Text>
              )}
            </VStack>

            {/* "حفظ التفاصيل" — per design (`Ticket Card 2 Contact First.png`) this is a button,
                and its job is to switch the card from this inputs view to the compact
                avatar/name/phone view. It is the manual-entry counterpart of confirming a pick, and
                the only path to collapsed for a hand-typed holder. */}
            <Button
              type='button'
              variant='outline'
              borderRadius='full'
              h='48px'
              w='100%'
              borderColor='dark.D150'
              color='dark.D800'
              onClick={onSaveDetails}
            >
              <FormattedMessage id='checkout.saveDetails' defaultMessage='Save details' />
            </Button>
          </VStack>
        )}
      </Box>
    </Flex>
  );
};
